import React from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
} from '@mui/material';
import { HeartIcon } from 'components/SVG';
import FavouritePopUp from 'components/common/Favourite';
import { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import MemoryBulbIcon from 'components/SVG/MemoryBulbIcon';
import { SparkEllipsisTypography } from 'sharedComponents/UI';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import SparkBadges from 'components/common/SparkBadges';
import MemoryCardModal from './Modals/MemoryCardModal';
import { useCurrentUser, useDialog } from 'hooks/common';
import {
  useCreateFavouriteList,
  useDeleteMemoryFavourite,
} from 'hooks/Favourites';
import { useCreateMemoryFavourite } from 'hooks/Favourites/useCreateMemoryFavourite';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import type { JoinedMemory } from 'types/Memory';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import { SparkBadge } from 'types/common/SparkBadge';
import { SPARK_BADGE_TYPES } from 'constants/common';
import { useUpdateMemory } from 'hooks/memory/useUpdateMemory';
import { getCurrentDateISOString } from 'utilities/time';

type SparkCardType = {
  joinedMemory: JoinedMemory;
  isParentLoading?: boolean;
};

function MemoryCard({ joinedMemory, isParentLoading = false }: SparkCardType) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const { currentUserID } = useCurrentUser();
  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();
  const { mutateAsync: addUserFlag } = useAddUserFlag();
  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();
  const { mutateAsync: updateMemory } = useUpdateMemory(joinedMemory.id);

  const {
    mutateAsync: createMemoryFavorites,
    isLoading: isCreatingMemoryFavourite,
  } = useCreateMemoryFavourite(joinedMemory.id);
  const {
    mutateAsync: deleteMemoryFavorites,
    isLoading: isDeletingMemoryFavourite,
  } = useDeleteMemoryFavourite(joinedMemory.id);

  const handleClose = () => {
    setAnchorEl(null);
  };
  const {
    openDialog: openMemoryCardDialog,
    closeDialog: closeMemoryCardDialog,
    open: isMemoryCardDialogOpen,
  } = useDialog();

  const allowedToReview = joinedMemory.meta.user.permissions?.canReview;

  const open = Boolean(anchorEl);
  const existingFavouriteList = joinedMemory?.meta.user.favouriteLists;
  const memoryFavourites = joinedMemory?.memoryFavourite;

  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    await createMemoryFavorites({
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    });
    handleClose();
  };
  const handleFavouriteListItem = async (listID: number) => {
    const foundExistingFavouriteList = memoryFavourites?.find(
      (favourite) => favourite.attributes.favouriteListID === listID
    );
    if (foundExistingFavouriteList) {
      await deleteMemoryFavorites(`${foundExistingFavouriteList.id}`);
      return;
    }
    await createMemoryFavorites({
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: listID,
    });
  };

  const handleLikeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      memoryID: joinedMemory?.id,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };

  const isLoadingMemoryFavourites =
    isCreatingMemoryFavourite || isDeletingMemoryFavourite;

  const badgesCategoryMap = joinedMemory?.meta?.badges.reduce<
    Record<string, SparkBadge[]>
  >((acc, curr) => {
    if (acc[curr.type]) {
      return { ...acc, [curr.type]: [...acc[curr.type], curr] };
    }

    return { ...acc, [curr.type]: [curr] };
  }, {});

  const handleDeleteMemory = async () => {
    await updateMemory({
      deletedAt: getCurrentDateISOString(),
      isDeleted: true,
    });
  };

  const handleArchiveMemory = async () => {
    await updateMemory({
      isArchived: true,
    });
  };
  const userPermissions = joinedMemory?.meta.user.permissions;

  return (
    <>
      <Card
        onClick={openMemoryCardDialog}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          borderRadius: '12px',
          transition: 'transform 0.1s',
          width: '100%',
          height: 260,
          border: (theme) => `1px solid ${theme.palette.borderColor}`,

          '&:hover': {
            borderColor: (theme) => theme.palette.primary.main,
          },
          '&:active': { transform: 'scale(0.98)' },
        }}
        elevation={0}
      >
        <CardHeader
          avatar={
            <SparkAvatarBackground
              width='66px'
              height='66px'
              icon={<MemoryBulbIcon width={'46.84px'} height={'41.64px'} />}
              src={joinedMemory?.attributes.iconURL || ''}
            />
          }
          action={
            <IconButton onClick={handleLikeClick}>
              {joinedMemory?.attributes.isFavouritedByCurrentUser ? (
                <HeartIcon color='#EF5251' fill='#EF5251' />
              ) : (
                <HeartIcon />
              )}
            </IconButton>
          }
          title={
            <SparkEllipsisTypography
              title={joinedMemory?.attributes.name || '- -'}
              sx={{ fontFamily: 'Mont-SemiBold' }}
            >
              {joinedMemory?.attributes.name || '- -'}
            </SparkEllipsisTypography>
          }
          subheader={
            <SparkBadges
              badges={badgesCategoryMap?.[SPARK_BADGE_TYPES.Creator] || []}
            />
          }
          sx={{ paddingBottom: 0 }}
        />

        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            paddingY: 0,
          }}
        >
          <SparkBadges
            badges={badgesCategoryMap?.[SPARK_BADGE_TYPES.Info] || []}
            containerStyles={{ paddingY: 1 }}
          />

          <SparkEllipsisTypography
            title={joinedMemory?.attributes.description}
            variant='body2'
            sx={{ WebkitLineClamp: 4 }}
          >
            {joinedMemory?.attributes.description}
          </SparkEllipsisTypography>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between' }}>
          <Box display='flex' width='100%' flexWrap='nowrap' gap={1}>
            <SparkBadges
              badges={badgesCategoryMap?.[SPARK_BADGE_TYPES.Category] || []}
              chipLabelStyles={{ paddingRight: 2, paddingLeft: 2 }}
              containerStyles={{ flex: 1 }}
            />
            <Box
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            >
              <SparkOptionsModal
                resourceLogoURL={joinedMemory?.attributes.iconURL || ''}
                options={joinedMemory?.meta.options}
                resourceAccessLinkToken={
                  joinedMemory?.attributes.accessLinkToken
                }
                privacyTypeId={joinedMemory?.attributes.memoryPrivacyTypeID}
                resourceID={joinedMemory?.id}
                handleSparkRatingSave={(() => {}) as any}
                userRating={undefined}
                allowedToReview={allowedToReview}
                resourceType='memory'
                onFavouriteItemClick={handleFavouriteListItem}
                existingFavouriteList={existingFavouriteList || []}
                onCreateList={handleCreateList}
                isLoading={isLoadingMemoryFavourites}
                onDelete={handleDeleteMemory}
                onArchive={handleArchiveMemory}
                flagReasonsDropdownOptions={flagReasonsDropdownOptions}
                onSendReport={handleSendReport}
                isResourceFavourite={joinedMemory?.meta.user.isFavourited}
                showHeartIcon={false}
                showShareIcon={false}
                userPermissions={userPermissions}
                resourceName={joinedMemory?.attributes?.name || '- -'}
              />
            </Box>
          </Box>
        </CardActions>
      </Card>
      {open && (
        <FavouritePopUp
          open={open}
          handleClose={handleClose}
          anchorEl={anchorEl}
          onFavouriteItemClick={handleFavouriteListItem}
          existingFavouriteList={existingFavouriteList || []}
          onCreateList={handleCreateList}
          isLoading={isLoadingMemoryFavourites}
        />
      )}
      {isMemoryCardDialogOpen && !!joinedMemory && (
        <MemoryCardModal
          isMemoryCardDialogOpen={isMemoryCardDialogOpen}
          closeMemoryCardDialog={closeMemoryCardDialog}
          memoryID={joinedMemory.id}
        />
      )}
    </>
  );
}

export default MemoryCard;
